<template>
  <ValidationProvider
    :class="['form__control', className]"
    :vid="vid"
    :rules="rules"
    :name="name"
    :mode="validationMode"
    :custom-messages="customMessages"
    tag="div"
    ref="provider"
    v-slot="{ errors, validated, invalid }"
  >
    <label :for="vid">
      <span :class="['label', labelClass]">
        {{ label }}
      </span>
      <div class="tooltip__wrap" v-if="tooltip">
        <v-tooltip top :min-width="tooltipMinWidth">
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <div class="hidden-sm-and-down text-link text--size-small">
                {{ tooltip.label }}
              </div>
              <span class="hidden-md-and-up text-link">
                {{ tooltip.label }}
              </span>
            </div>
          </template>
          <span class="text-regular text--size-small">
            {{ tooltip.content }}
          </span>
        </v-tooltip>
      </div>
    </label>
    <v-autocomplete
      :id="vid"
      :placeholder="placeholder"
      :items="formatListItems"
      :item-text="itemText"
      :item-value="itemValue"
      background-color="white"
      :error-messages="errors[0] || externalErrorMsg"
      :append-icon="displayValidationIcon(errors, externalErrorMsg, validated)"
      :hide-details="hideDetails"
      :autocomplete="autocomplete"
      :class="[
        'input__control',
        { uppercase: isUppercase, 'field-invalid': invalid },
      ]"
      v-model="innerValue"
      single-line
      outlined
      ref="input"
      @blur="handleBlur"
      :prefix="prefix"
      :dense="dense"
      :disabled="disabled"
      :readonly="readonly"
      :data-uiid="dataUiid"
      :height="height"
      :aria-label="ariaLabel"
      :menu-props="menuProps"
    >
      <template v-slot:message="{ message }">
        <span v-html="message"></span>
      </template>
      <template v-slot:item="{ item, on, attrs }">
        <v-list-item
          v-on="on"
          v-bind="attrs"
          :data-uiid="setItemDataUiid(item[itemText])"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item[itemText] }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:no-data>
        <v-list-item class="noHover">
          <v-list-item-content>
            <v-list-item-title>
              <p class="font-12 text-center ma-0">
                <strong>Sorry!</strong> {{ noDataMatchingMessage }}
              </p>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </ValidationProvider>
</template>
<script>
import { ValidationProvider } from "vee-validate";
import { generateAlphaSort } from "@/common/sorting";
import { capitalizeLetterText } from "@/common/utils";

export default {
  name: "BaseAutocomplete",
  components: {
    ValidationProvider,
  },
  props: {
    vid: String,
    value: String,
    className: String,
    name: String,
    fieldName: String,
    label: String,
    labelClass: String,
    externalErrorMsg: String,
    customMessages: Object,
    placeholder: String,
    items: Array,
    itemText: {
      type: String,
      default: "Description",
    },
    itemValue: {
      type: String,
      default: "Code",
    },
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    isUppercase: Boolean,
    autocomplete: String,
    tooltip: Object,
    rules: [Object, String],
    validationMode: {
      default: "eager",
      required: false,
    },
    prefix: String,
    dense: {
      type: Boolean,
      default: true,
    },
    hideValidationIcon: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    dataUiid: {
      type: String,
      default: "",
    },
    height: [String, Number],
    noDataMatchingMessage: {
      type: String,
      default: "We couldn't find any matching results.",
    },
    ariaLabel: String,
    isEnableCapitalizeLetterListItems: {
      type: Boolean,
      default: true,
    },
    isEnableSortOrderListItems: {
      type: Boolean,
      default: true,
    },
    menuProps: {
      type: Object,
      default: () => {
        return {
          bottom: true,
          offsetY: true,
          allowOverflow: true,
          offsetOverflow: false, // required for v-autocomplete known Vuetify bug
          contentClass: "autocomplete-dropdown",
        };
      },
    },
  },
  data: () => ({
    innerValue: "",
    tooltipMinWidth: 280,
    test: null,
  }),
  computed: {
    formatListItems() {
      if (!this.items) return [];
      // format item display text
      if (this.isEnableCapitalizeLetterListItems) {
        this.items.map((item) => {
          item[this.itemText] = capitalizeLetterText(item[this.itemText]);
        });
      }
      // Sort a clone of the array
      if (this.isEnableSortOrderListItems) {
        return this.items.map((x) => x).sort(generateAlphaSort(this.itemText));
      }
      return this.items;
    },
  },
  watch: {
    // Handles internal model changes.
    innerValue(value) {
      this.$emit("input", value, this.fieldName, this.$refs.provider);
    },
    // Handles external model changes.
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val;
      }
    },
  },
  created() {
    if (this.value) {
      this.innerValue = this.value;
    }
  },
  mounted() {
    // fix issue: tooltip width is not stable
    this.$nextTick(() => {
      this.tooltipMinWidth = this.$refs.input?.$el?.clientWidth;
    });
  },
  methods: {
    handleBlur(e) {
      this.$emit("blur", e.target.value, this.fieldName, this.$refs.provider);
    },
    displayValidationIcon(errors, externalErrorMsg, validated) {
      if (this.hideValidationIcon) {
        return "";
      }
      return errors[0] || externalErrorMsg
        ? "mdi mdi-alert-circle"
        : validated
        ? "icon-check"
        : "";
    },
    setItemDataUiid(val) {
      return val.replace(/\s+/g, "");
    },
  },
};
</script>
<style lang="scss" scoped>
@media screen and (min-width: $grid-breakpoints-xs) {
  .tooltip__wrap {
    float: right;
    margin-top: 2px;
  }
}
@media screen and (max-width: ($grid-breakpoints-xs - 1)) {
  .tooltip__wrap {
    display: block;
  }
}
:deep(.v-text-field__prefix) {
  font-weight: bold;
}

:deep(.v-input__append-inner) {
  margin: auto 0 !important;
  .v-icon.icon-check,
  .v-icon.mdi-alert-circle {
    width: 20px;
    height: 20px;
    font-size: 20px;
    transform: none !important;
  }
}
</style>
